import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Warning, Trophy } from '@phosphor-icons/react';

// Phase 5 — Admin Dashboard widget. Compact 1/3-column variant designed to sit
// next to "Today's Summary" / "Income vs Profit" in the Dashboard's Tier 2 row.

const fmtINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const STATUS_COLOR = {
  present: 'text-[#15803D]', absent: 'text-[#B91C1C]', half_day: 'text-[#9A3412]',
  late: 'text-[#92400E]', leave: 'text-[#6D28D9]', weekly_off: 'text-[#4338CA]', not_marked: 'text-[#A1A1AA]',
};

const BUCKET = {
  excellent: 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]',
  good: 'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]',
  average: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]',
  needs_improvement: 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]',
};

export default function StaffPulseWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/staff-dashboard/admin').then(r => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const t = data.today || {};
  const sp = data.salary_payable || {};
  const lates = data.alerts?.repeated_late || [];
  const pending = data.alerts?.long_pending || [];
  const hasAlerts = lates.length > 0 || pending.length > 0;

  return (
    <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="staff-pulse-widget">
      <CardHeader className="p-5 pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold">Staff Pulse · Today</CardTitle>
        <Button asChild variant="link" size="sm" className="text-[#3B82F6] text-xs p-0 h-auto">
          <Link to="/staff/reports" data-testid="staff-pulse-all-reports">All reports</Link>
        </Button>
      </CardHeader>
      <CardContent className="p-5 pt-0 space-y-3">
        {/* 7 attendance buckets in a 4-col compact grid */}
        <div className="grid grid-cols-4 gap-1.5">
          {[
            ['Present', t.present, 'present'],
            ['Absent', t.absent, 'absent'],
            ['Half', t.half_day, 'half_day'],
            ['Late', t.late, 'late'],
            ['Leave', t.leave, 'leave'],
            ['Off', t.weekly_off, 'weekly_off'],
            ['Unmarked', t.not_marked, 'not_marked'],
          ].map(([label, val, k]) => (
            <div key={k} className="border border-[#E4E4E7] rounded-lg p-1.5 text-center">
              <p className="text-[8px] uppercase tracking-[0.1em] text-[#A1A1AA] truncate">{label}</p>
              <p className={`text-lg font-bold font-mono leading-tight ${STATUS_COLOR[k]}`}>{val ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Salary payable strip */}
        <div className="border border-[#E4E4E7] rounded-lg p-2.5 flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Salary Payable · {data.month}</p>
            <p className="text-[10px] text-[#A1A1AA] mt-0.5">{sp.pending_count || 0} pending · {sp.paid_count || 0} paid</p>
          </div>
          <p className="text-lg font-bold font-mono text-[#D97706]">{fmtINR(sp.pending_amount)}</p>
        </div>

        {/* Top performers */}
        <div className="border border-[#E4E4E7] rounded-lg p-2.5">
          <p className="text-[9px] uppercase tracking-[0.12em] font-semibold text-[#52525B] flex items-center gap-1.5 mb-1.5">
            <Trophy size={11} className="text-[#F59E0B]" /> Top Performers
          </p>
          <div className="space-y-1">
            {(data.top_performers || []).slice(0, 3).map(s => (
              <div key={s.user_id} className="flex items-center justify-between text-xs">
                <span className="truncate pr-2">{s.user_name}</span>
                <Badge className={`rounded-md text-[10px] ${BUCKET[s.bucket]}`} variant="outline">{s.score}</Badge>
              </div>
            ))}
            {(data.top_performers || []).length === 0 && (
              <p className="text-xs text-[#A1A1AA] italic">No data yet</p>
            )}
          </div>
        </div>

        {/* Alerts (only when present) */}
        {hasAlerts && (
          <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-2.5 space-y-1">
            <p className="text-[9px] uppercase tracking-[0.12em] font-semibold text-[#92400E] flex items-center gap-1.5">
              <Warning size={11} /> Alerts
            </p>
            {lates.slice(0, 2).map(l => (
              <p key={l.user_id} className="text-[11px] text-[#92400E] leading-snug">
                <span className="font-semibold">{l.user_name}</span> · {l.late_entries} late entries
              </p>
            ))}
            {pending.length > 0 && (
              <p className="text-[11px] text-[#92400E] leading-snug">
                <span className="font-semibold">{pending.length}</span> task{pending.length > 1 ? 's' : ''} pending &gt; 7d
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
